import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  ScanSearch, Search, Loader2, CheckCircle2, XCircle, AlertTriangle, Globe, Share2,
  Smartphone, ShieldCheck, Star, Lightbulb, Bookmark, BookmarkCheck, Copy, ExternalLink,
  RefreshCw, Sparkles, Info, Rocket,
} from "lucide-react";
import { AIResourcesShell } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import { BuilderLogo } from "../../components/BuilderLogo";
import { aiNameCheck, aiNameSuggestions, aiSave } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useAIResources } from "../../hooks/useAIResources";
import { copyToClipboard } from "../../lib/clipboard";

const SOCIAL_URL = {
  "Instagram": "https://instagram.com", "X (Twitter)": "https://x.com", "Facebook": "https://facebook.com",
  "TikTok": "https://tiktok.com", "LinkedIn": "https://linkedin.com", "YouTube": "https://youtube.com",
};

const StatusTag = ({ status }) => {
  const map = {
    available: { c: "text-green-600", i: CheckCircle2, t: "Available" },
    taken: { c: "text-red-500", i: XCircle, t: "Taken" },
    similar: { c: "text-amber-500", i: AlertTriangle, t: "Similar" },
    clear: { c: "text-green-600", i: CheckCircle2, t: "Clear" },
    possible: { c: "text-amber-500", i: AlertTriangle, t: "Possible match" },
    unknown: { c: "text-[#9aa1ab]", i: Search, t: "Check" },
  };
  const s = map[status] || map.available;
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${s.c}`}><s.i size={14} /> {s.t}</span>;
};

const REGISTRARS = [
  { name: "Namecheap", url: (d) => `https://www.namecheap.com/domains/registration/results/?domain=${d}` },
  { name: "GoDaddy", url: (d) => `https://www.godaddy.com/domainsearch/find?domainToCheck=${d}` },
  { name: "Porkbun", url: (d) => `https://porkbun.com/checkout/search?q=${d}` },
  { name: "Cloudflare", url: () => `https://dash.cloudflare.com/?to=/:account/domains/register` },
  { name: "Squarespace Domains", url: (d) => `https://domains.squarespace.com/domain-search?query=${d}` },
];

const RegisterModal = ({ domain, onClose }) => (
  <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="nc-register-modal">
    <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">Register</p>
          <p className="font-display text-xl font-extrabold text-[#0F0F0F]">{domain}</p>
        </div>
        <button onClick={onClose} className="text-[#687280] hover:text-[#0F0F0F]" data-testid="nc-register-modal-close"><XCircle size={20} /></button>
      </div>
      <p className="text-sm text-[#687280] mt-2">Choose where you'd like to register this domain:</p>
      <div className="mt-4 space-y-2">
        {REGISTRARS.map((r) => (
          <a key={r.name} href={r.url(domain)} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-4 py-3 hover:border-[#D4AF37] transition-colors group" data-testid={`nc-registrar-${r.name.split(" ")[0].toLowerCase()}`}>
            <span className="flex items-center gap-2.5"><BuilderLogo url={r.url(domain)} name={r.name} size={24} /><span className="font-semibold text-sm text-[#0F0F0F]">{r.name}</span></span>
            <ExternalLink size={15} className="text-[#9aa1ab] group-hover:text-[#D4AF37]" />
          </a>
        ))}
      </div>
    </div>
  </div>
);

const ScoreRing = ({ score }) => {
  const r = 58, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg width="160" height="160" className="-rotate-90">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#f1eddb" strokeWidth="12" />
        <circle cx="80" cy="80" r={r} fill="none" stroke="#D4AF37" strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .9s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-extrabold text-[#0F0F0F]">{score}</span>
        <span className="text-xs text-[#9aa1ab]">/100</span>
      </div>
    </div>
  );
};

const SUB_LABELS = { memorability: "Memorability", simplicity: "Simplicity", pronunciation: "Pronunciation", uniqueness: "Uniqueness", brandability: "Brandability", professionalism: "Professionalism" };

export default function NameChecker() {
  const { user, requireAuth } = useAuth();
  const { nameChecker, patchNameChecker } = useAIResources();
  const { name, result } = nameChecker;
  const [input, setInput] = useState(name || "");
  const [loading, setLoading] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [regDomain, setRegDomain] = useState(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [excludeList, setExcludeList] = useState([]);

  useEffect(() => { setInput(name || ""); }, [name]);
  // Reset the "already seen" exclusion set whenever a fresh name is checked.
  useEffect(() => { setExcludeList(result?.alternatives || []); }, [result?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateMore = async () => {
    if (!result || moreLoading) return;
    setMoreLoading(true);
    try {
      const d = await aiNameSuggestions(result.name, excludeList);
      const fresh = (d.alternatives || []).filter((a) => !excludeList.some((x) => x.toLowerCase() === a.toLowerCase()));
      if (fresh.length === 0) { toast.info("No new names right now — try again"); }
      else {
        patchNameChecker({ result: { ...result, alternatives: fresh } }); // replace, don't append
        setExcludeList((prev) => [...prev, ...fresh]);
        toast.success("Fresh set of names generated");
      }
    } catch { toast.error("Could not generate more names"); }
    finally { setMoreLoading(false); }
  };

  const search = async (q) => {
    const n = (q ?? input).trim();
    if (!n) { toast.error("Enter a startup or business name"); return; }
    setLoading(true); setSavedFlag(false);
    try {
      const r = await aiNameCheck(n);
      patchNameChecker({ name: n, result: r });
      setTimeout(() => document.getElementById("nc-results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch { toast.error("Could not check that name. Try again."); }
    finally { setLoading(false); }
  };

  const saveResult = () => {
    if (!result) return;
    if (savedFlag) { toast.info("Already saved to your Startup Notebook"); return; }
    const doSave = async () => {
      try { await aiSave("name", result); setSavedFlag(true); toast.success("Saved to your Startup Notebook"); }
      catch { toast.error("Could not save"); }
    };
    user ? doSave() : requireAuth(doSave);
  };

  const copyResults = async () => {
    if (!result) return;
    const lines = [
      `Startup Name Check — ${result.name}`,
      `AI Brand Score: ${result.brand_score}/100`,
      `Business name: ${result.business.status}`,
      `Domains: ${result.domains.map((d) => `${d.domain} (${d.status})`).join(", ")}`,
      `Recommended domain: ${result.recommended_domains.join(", ") || "—"}`,
      `Alternatives: ${result.alternatives.join(", ")}`,
    ].join("\n");
    (await copyToClipboard(lines)) ? toast.success("Results copied") : toast.error("Copy failed");
  };

  const registerDomain = () => {
    const d = result?.domains.find((x) => x.status === "available") || result?.domains[0];
    if (d) setRegDomain(d.domain);
  };

  const searchAgain = () => { patchNameChecker({ result: null }); setInput(""); setSavedFlag(false); document.getElementById("nc-search")?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <AIResourcesShell active="name-checker" title="Startup Name Checker" subtitle="Check your startup name across domains, social media, app stores and trademarks — and get an AI Brand Score, all in one place." icon={ScanSearch}>
      {/* Search */}
      <section id="nc-search" className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="nc-search-section">
        <label className="text-sm font-semibold text-[#0F0F0F]">Enter your startup or business name</label>
        <div className="flex flex-col sm:flex-row gap-3 mt-3">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="LaunchBS" className="flex-1 rounded-full border border-gray-200 px-5 py-3.5 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="nc-input" />
          <button onClick={() => search()} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-7 py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="nc-search-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search Name
          </button>
        </div>
        <p className="text-xs text-[#9aa1ab] mt-3 flex items-center gap-1.5"><Lightbulb size={13} className="text-[#D4AF37]" /> Tip: Keep it short, unique and easy to remember.</p>
      </section>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 text-[#687280]" data-testid="nc-loading">
          <div className="relative w-16 h-16">
            <span className="absolute inset-0 rounded-full bg-[#D4AF37]/25 animate-ping" />
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] flex items-center justify-center"><ScanSearch size={26} /></div>
          </div>
          <p className="mt-5 text-sm font-semibold text-[#0F0F0F]">Checking availability & scoring your name…</p>
        </div>
      )}

      {result && !loading && (
        <ResultPaywall label="name check results" testid="nc-paywall">
        <div id="nc-results" className="mt-6" data-testid="nc-results">
          {result.live
            ? <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-green-50 border border-green-200 text-green-700 px-2.5 py-1 rounded-full mb-4" data-testid="nc-live-badge"><CheckCircle2 size={12} /> LIVE results</span>
            : <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-[#FBF7EA] border border-[#D4AF37]/40 text-[#8a6d1a] px-2.5 py-1 rounded-full mb-4">BETA · could not verify live, showing estimate</span>}
          <div className="grid lg:grid-cols-[1fr_360px] gap-5">
            {/* LEFT */}
            <div className="space-y-5">
              {/* Business name */}
              <Card icon={CheckCircle2} title="1. Business Name Availability" tag={<StatusTag status={result.business.status} />} testid="nc-business">
                <p className="text-sm text-[#374151] mt-2">{result.business.message}</p>
              </Card>

              {/* Domains */}
              <Card icon={Globe} title="2. Domain Availability" testid="nc-domains">
                <div className="mt-3 divide-y divide-gray-50">
                  {result.domains.map((d) => (
                    <div key={d.ext} className="flex items-center gap-3 py-2.5" data-testid={`nc-domain-${d.ext.slice(1)}`}>
                      <span className="font-mono text-sm text-[#0F0F0F] flex-1">{d.domain}</span>
                      <StatusTag status={d.status} />
                      {d.status === "taken" ? (
                        <a href={`https://who.is/whois/${d.domain}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold border border-gray-200 rounded-full px-3 py-1.5 hover:border-[#D4AF37] text-[#374151]">Whois</a>
                      ) : (
                        <button onClick={() => setRegDomain(d.domain)} className="text-xs font-bold border border-gray-200 rounded-full px-3 py-1.5 hover:border-[#D4AF37] text-[#374151]" data-testid={`nc-domain-register-${d.ext.slice(1)}`}>Register</button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Socials */}
              <Card icon={Share2} title="3. Social Media Availability" testid="nc-socials">
                <div className="mt-3 divide-y divide-gray-50">
                  {result.socials.map((s) => (
                    <div key={s.platform} className="flex items-center gap-3 py-2.5" data-testid={`nc-social-${s.platform.split(" ")[0].toLowerCase()}`}>
                      <BuilderLogo url={SOCIAL_URL[s.platform]} name={s.platform} size={26} />
                      <span className="text-sm font-medium text-[#0F0F0F] flex-1">{s.platform}</span>
                      <StatusTag status={s.status} />
                      <span className="text-xs text-[#9aa1ab] hidden sm:inline w-28 truncate text-right">{s.handle}</span>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold border border-gray-200 rounded-full px-3 py-1.5 hover:border-[#D4AF37] text-[#374151]">View</a>
                    </div>
                  ))}
                </div>
              </Card>

              {/* App store + Trademark */}
              <div className="grid sm:grid-cols-2 gap-5">
                <Card icon={Smartphone} title="4. App Store Search" testid="nc-appstore">
                  <div className="mt-3 space-y-3">
                    {result.appstores.map((a) => (
                      <div key={a.store}>
                        <p className="text-sm font-semibold text-[#0F0F0F]">{a.store}</p>
                        <p className={`text-xs mt-0.5 flex items-center gap-1.5 ${a.status === "clear" ? "text-green-600" : "text-amber-500"}`}>{a.status === "clear" ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />} {a.message}</p>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card icon={ShieldCheck} title="5. Trademark Check" testid="nc-trademark">
                  <p className={`text-sm mt-3 font-semibold flex items-center gap-1.5 ${result.trademark.status === "clear" ? "text-green-600" : "text-amber-500"}`}>{result.trademark.status === "clear" ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />} {result.trademark.status === "clear" ? "No obvious trademark conflict found" : "Possible trademark match found"}</p>
                  <p className="text-xs text-[#687280] mt-1.5">{result.trademark.message}</p>
                  <p className="mt-3 text-[11px] text-[#8a6d1a] bg-[#FBF7EA] border border-[#D4AF37]/30 rounded-lg p-2.5"><Info size={12} className="inline mr-1" />{result.trademark.disclaimer}</p>
                </Card>
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-5">
              {/* Brand score */}
              <div className="rounded-3xl border border-gray-100 bg-white p-6" data-testid="nc-brand-score">
                <p className="font-display font-bold text-[#0F0F0F] flex items-center gap-2"><Star size={17} className="text-[#D4AF37] fill-[#D4AF37]" /> AI Brand Score</p>
                <ScoreRing score={result.brand_score} />
                <div className="flex justify-center gap-1 mt-1">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={18} className={i <= result.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-gray-200 fill-gray-200"} />)}</div>
                <p className="text-center text-sm font-bold text-[#8a6d1a] mt-2">{result.brand_score >= 85 ? "Excellent" : result.brand_score >= 70 ? "Strong" : result.brand_score >= 50 ? "Decent" : "Needs Work"} Brand Potential</p>
                <div className="mt-4 space-y-2.5">
                  {Object.entries(SUB_LABELS).map(([k, label]) => (
                    <div key={k}>
                      <div className="flex justify-between text-xs"><span className="text-[#374151] font-medium">{label}</span><span className="font-bold text-[#0F0F0F]">{result.subscores[k]}/100</span></div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-1"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${result.subscores[k]}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Feedback */}
              <div className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#FBF7EA]/60 to-white p-6" data-testid="nc-feedback">
                <p className="font-display font-bold text-[#0F0F0F] flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> AI Feedback</p>
                <p className="text-sm text-[#374151] mt-2 leading-relaxed">{result.feedback}</p>
                {result.recommended_domains.length > 0 && (
                  <>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a] mt-4 mb-2">Recommended</p>
                    <div className="flex flex-wrap gap-2">{result.recommended_domains.map((d) => <span key={d} className="rounded-full bg-white border border-[#D4AF37]/40 px-3 py-1.5 text-xs font-bold text-[#0F0F0F]">{d}</span>)}</div>
                  </>
                )}
              </div>

              {/* Grab It Fast */}
              <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6" data-testid="nc-grab-fast">
                <p className="font-display font-bold flex items-center gap-2"><Rocket size={17} className="text-[#D4AF37]" /> Grab It Fast</p>
                <p className="text-xs text-[#c9c39f] mt-1">Lock in your name before someone else does.</p>
                {(() => {
                  const d = result.domains.find((x) => x.status === "available");
                  return d ? (
                    <div className="mt-4 flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 p-3">
                      <span className="font-mono text-sm truncate">{d.domain}</span>
                      <button onClick={() => setRegDomain(d.domain)} className="shrink-0 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-4 py-1.5 text-xs font-bold hover:bg-[#c5a030]" data-testid="nc-grab-domain">Register</button>
                    </div>
                  ) : <p className="mt-3 text-xs text-[#c9c39f]">No open domains — try an alternative name below.</p>;
                })()}
                <p className="text-[11px] font-bold uppercase tracking-wide text-[#D4AF37] mt-4 mb-2">Claim Your Handles</p>
                <div className="flex flex-wrap gap-2">
                  {result.socials.filter((s) => s.status === "available").map((s) => (
                    <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold transition-colors" data-testid={`nc-grab-social-${s.platform.split(" ")[0].toLowerCase()}`}>
                      <BuilderLogo url={SOCIAL_URL[s.platform]} name={s.platform} size={16} tile={false} /> {s.platform}
                    </a>
                  ))}
                  {result.socials.filter((s) => s.status === "available").length === 0 && <span className="text-xs text-[#c9c39f]">No confirmed open handles right now.</span>}
                </div>
              </div>

              {/* Alternatives */}
              <div className="rounded-3xl border border-gray-100 bg-white p-6" data-testid="nc-alternatives">
                <p className="font-display font-bold text-[#0F0F0F] flex items-center gap-2"><Lightbulb size={16} className="text-[#D4AF37]" /> Suggested Alternatives</p>
                <ol className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {result.alternatives.map((a, i) => (
                    <li key={i} className="flex gap-2"><span className="text-[#9aa1ab] font-semibold w-5">{i + 1}.</span><button onClick={() => { setInput(a); search(a); }} className="text-[#0F0F0F] hover:text-[#8a6d1a] font-medium text-left">{a}</button></li>
                  ))}
                </ol>
                <button onClick={generateMore} disabled={moreLoading} className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#7c3aed] hover:text-[#5b21b6] disabled:opacity-60" data-testid="nc-generate-more">{moreLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Generate More Names</button>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-6 rounded-3xl border border-gray-100 bg-white p-5" data-testid="nc-quick-actions">
            <p className="font-display font-bold text-[#0F0F0F] mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> Quick Actions</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <button onClick={saveResult} className="inline-flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3 text-sm font-bold text-[#374151] hover:border-[#D4AF37]" data-testid="nc-save-btn">{savedFlag ? <BookmarkCheck size={15} className="text-[#D4AF37]" /> : <Bookmark size={15} />} {savedFlag ? "Saved" : "Save to Notebook"}</button>
              <button onClick={copyResults} className="inline-flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3 text-sm font-bold text-[#374151] hover:border-[#D4AF37]" data-testid="nc-copy-btn"><Copy size={15} /> Copy Results</button>
              <button onClick={registerDomain} className="inline-flex items-center justify-center gap-2 border border-gray-200 rounded-full py-3 text-sm font-bold text-[#374151] hover:border-[#D4AF37]" data-testid="nc-register-btn"><ExternalLink size={15} /> Register Domain</button>
              <button onClick={searchAgain} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030]" data-testid="nc-again-btn"><Search size={15} /> Search Another Name</button>
            </div>
          </div>

          <p className="mt-4 text-xs text-[#687280] bg-[#F7F7F7] rounded-xl p-3 flex items-start gap-2"><Info size={14} className="mt-0.5 shrink-0 text-[#9aa1ab]" /> Note: Availability can change. We recommend registering your domain and social handles as soon as possible.</p>
        </div>
        </ResultPaywall>
      )}

      {regDomain && <RegisterModal domain={regDomain} onClose={() => setRegDomain(null)} />}
    </AIResourcesShell>
  );
}

const Card = ({ icon: Icon, title, tag, children, testid }) => (
  <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.02)]" data-testid={testid}>
    <div className="flex items-center justify-between gap-3">
      <p className="font-display font-bold text-[#0F0F0F] flex items-center gap-2"><Icon size={17} className="text-[#D4AF37]" /> {title}</p>
      {tag}
    </div>
    {children}
  </div>
);
